/* istanbul ignore file */
import { nanoid } from 'nanoid'
import { useEffect } from 'react'
import base64ToBlob from 'base64toblob'

const pendingHandlers = {}

export const execute = (action, data = null) => {
  return new Promise((resolve, reject) => {
    const handlerId = `${action}_CB_${nanoid()}`

    pendingHandlers[handlerId] = { resolve, reject }

    postMessage(action, handlerId, data)
  })
}

export const hasFeature = feature => {
  return new Promise(resolve => {
    if (!isNativeBridgeAvailable()) return resolve(false)

    execute('getFeatures')
      .then(({ features }) => {
        if (features.includes(feature)) {
          return resolve(true)
        }

        resolve(false)
      })
      .catch(() => resolve(false))
  })
}

export const postMessage = (action, handlerId, data) => {
  if (!isNativeBridgeAvailable()) {
    return
  }

  // Post Android message
  if (isAndroidBridgeAvailable()) {
    if (typeof window.ticketSwapAndroid[action] === 'undefined') {
      return
    }

    window.ticketSwapAndroid[action](handlerId, JSON.stringify(data))
  }

  // Post iOS message
  if (isIOSBridgeAvailable()) {
    const payload = JSON.stringify({
      handlerId,
      action,
      data,
    })

    try {
      window.webkit.messageHandlers.ticketswap &&
        window.webkit.messageHandlers.ticketswap.postMessage(payload)
    } catch (error) {
      console.log(error)
    }
  }
}

export const isIOSBridgeAvailable = () => {
  if (
    typeof window.webkit === 'undefined' ||
    typeof window.webkit.messageHandlers === 'undefined' ||
    typeof window.webkit.messageHandlers.ticketswap === 'undefined'
  ) {
    return false
  }

  return true
}

export const isAndroidBridgeAvailable = () => {
  if (typeof window.ticketSwapAndroid === 'undefined') {
    return false
  }

  return true
}

export const isNativeBridgeAvailable = () => {
  return isAndroidBridgeAvailable() || isIOSBridgeAvailable()
}

// Event listener handler
const handleNativeEvent = event => {
  const { handlerId, data, action } = event.detail

  if (typeof pendingHandlers[handlerId] === 'undefined') {
    return
  }

  if (action === 'reject') {
    pendingHandlers[handlerId].reject(data)
  } else {
    pendingHandlers[handlerId].resolve(data)
  }

  delete pendingHandlers[handlerId]
}

// React hook to register event listener
export const useNativeBridge = () => {
  useEffect(() => {
    window.addEventListener('ticketswap-native-bridge', handleNativeEvent)

    return () => {
      window.removeEventListener('ticketswap-native-bridge', handleNativeEvent)
    }
  }, [])
}

// Component used in app
export const NativeBridge = () => {
  useNativeBridge()
  return null
}

const acceptRejectFiles = (name, type, content) => {
  const accepted = []
  const rejected = []
  const blob = base64ToBlob(content, type)

  blob.name = name

  if (/pdf|pkpass|octet-stream/.test(type)) {
    accepted.push(blob)
  } else {
    rejected.push(blob)
  }

  return [accepted, rejected]
}

// Calls implementations
export const importFile = () => {
  return execute('importFile').then(({ name, type, content }) => {
    return acceptRejectFiles(name, type, content)
  })
}

export const importOpenedFile = async () => {
  try {
    const { name, type, content } = await execute('importOpenedFile')
    const [accepted] = acceptRejectFiles(name, type, content)
    return accepted
  } catch (error) {
    return []
  }
}
